import { useRouter } from 'next/router';
import { editorialCategoryProps } from '@/components/Home/FeaturedEditorialCategory';
import classNames from '@/utils/className';
import { editorialCategoryType, getEditorialCategory } from '@/lib/sponsors';
import { publicRouteFor } from '@/lib/editorialRoutes';
import ImageComponent from '../Image';

interface SponsorBannerProps {
  isInternal?: boolean;
  isFeatured?: boolean;
  variant: editorialCategoryType;
  isAdvertorial?: boolean;
}

const variants = {
  EditorialCategoryLogoContainer: {
    isInternal: {
      true: 'isInternal rounded-t-lg rounded-b-none [&.isInternal]:tablet:pb-6 [&.isInternal]:tablet:pb-6 [&>svg]:w-[108px] [&>a>svg]:w-[108px] [&>svg]:h-[33px] [&>a>svg]:h-[33px] p-4',
      false: '',
    },
    isFeatured: {
      true: 'isFeatured [&.isFeatured]:py-4 [&.isFeatured]:px-0',
      false: '',
    },
    backgroundColor: {
      ctup: 'bg-[#004BCC]',
      eletro: 'bg-[#1C1C1C]',
      ctauto: 'bg-[#E0140F]',
    },
  },
  EditorialCategorySponsorContainer: {
    editorialCategory: {
      ctup: 'flex flex-col before:block before:text-mglGray50 text-sm before:content-[attr(data-before)]',
      eletro: 'flex flex-col before:block before:text-mglGray50 text-sm before:content-[attr(data-before)]',
    },
  },
} as const;

const SponsorContainer = ({ editorialCategory }: { editorialCategory: SponsorBannerProps['variant'] }) => {
  const responsiveLogoClass = 'first:[&>figure]:block';

  const options = {
    ctup: (
      <span data-before="Conteúdo apoiado por:" className={classNames('flex', variants.EditorialCategorySponsorContainer.editorialCategory.ctup)}>
        <div className="mt-2 flex justify-center gap-4">
          <a
            className={classNames('z-[2] flex items-center', responsiveLogoClass)}
            href="https://canalte.ch/tcl"
            rel="sponsored"
            title="Ir para o site da TCL"
            target="_blank"
          >
            <ImageComponent
              src="https://t.ctcdn.com.br/7z5J3EB8Py_zsjQkllq9sPl826k=/i1126385.png"
              blurhash="LVT8zXs:cYt7xaV@WVWVqFWVQ-WB"
              alt="TCL"
              height={39}
              width={64}
              rounded={false}
            />
          </a>
        </div>
      </span>
    ),
    eletro: null,
    ctauto: (
      <div className="z-[2] flex items-center first:block first:h-8 first:w-8 last:hidden last:h-[30px] last:w-[88px] tablet:block first:tablet:hidden" />
    ),
  };

  return options[editorialCategory];
};

export default function SponsorBanner({ isInternal = undefined, isFeatured = undefined, variant, isAdvertorial = false }: SponsorBannerProps) {
  const router = useRouter();
  const hasLogo = !!editorialCategoryProps[variant]?.logo;
  const EditorialCategoryLogo = () => {
    // Na própria página da editoria o logo não vira link (o usuário já está lá).
    // Normaliza o slug da URL atual para variant — cobre o slug público (ct-eletro)
    // e os aliases, em vez de comparar o slug cru contra os nomes de variant.
    const currentSlug = Array.isArray(router.query.categorias) ? router.query.categorias[0] : router.query.categorias;
    if (currentSlug && getEditorialCategory(currentSlug) === variant) {
      return <>{editorialCategoryProps[variant]?.logo}</>;
    }
    if (hasLogo) {
      return <a href={`/${publicRouteFor(variant)}/`}>{editorialCategoryProps[variant]?.logo}</a>;
    }
    return null;
  };

  return (
    <div
      className={classNames(
        'flex flex-col items-center justify-center gap-4 px-6 py-4 tablet:flex-row tablet:justify-between tablet:pb-12 [&>a]:h-8 [&>svg]:h-8',
        isFeatured && variants.EditorialCategoryLogoContainer.isFeatured[isFeatured.toString()],
        isInternal && variants.EditorialCategoryLogoContainer.isInternal[isInternal.toString()],
        variants.EditorialCategoryLogoContainer.backgroundColor[variant]
      )}>
      {hasLogo && <EditorialCategoryLogo />}
      {!isAdvertorial && <SponsorContainer editorialCategory={variant} />}
    </div>
  );
}
